import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, Inject } from 'ng-metadata/core';

import './date-picker.scss';

/**
 * @ngdoc directive
 * @name mwDatePicker
 * @module mw.components.datePicker
 *
 * @param {Date} [date] - 'The date that was picker is listening on'
 * @param {string} [locale] - 'The language that the date picker will be shown in'
 * @param {string} [min-view] - 'The starting view of when the user opens up the date picker'
 * @param {string} [max-view] - 'How deep the user can go into the calendar all the way to seconds'
 * @param {string} [format] - 'The format of how the date will be presented'
 * @param {string} [start-view] - 'View in which the calendar starts at'
 * @param {Date} [min-date] - 'The minimum date that the user can go down to'
 * @param {Date} [max-date] - 'The maximum date that the user can go up to'
 * @param {Date} [start-date] - 'The starting date of the selection'
 * @param {Date} [end-date] - 'The end date of the selection'
 * @param {Date} [active-view-date] - 'The maximum date that the user can go up to'
 * @param {string} [start-view] - Initial level of detail for the date picker.
 * @param {string} [timezone] - Timezone to be used by date picker. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 * @param {EventEmitter} [on-date-selection] - 'What to do when the date is selected'
 * @usage
 * <hljs hljs-language="html">
 *  <mw-date-picker
 *      date="new Date()"
 *      locale="en"
 *      min-view="month"
 *      max-view="month"
 *      start-view="month"
 *      format="L LTS"
 *      on-date-selection="$ctrl.handleDateChange($event)"
 * > </mw-date-picker>
 * </hljs>
 */
@Component({
    selector: 'mw-date-picker',
    template: `
        <div class="mw-date-picker">
            <div
                moment-picker
                ng-model="$ctrl.selectedMoment"
                locale="{{$ctrl.locale}}"
                min-view="{{$ctrl.minView}}"
                max-view="{{$ctrl.maxView}}"
                min-date="$ctrl.minMoment"
                max-date="$ctrl.maxMoment"
                start-date="$ctrl.activeViewMoment"
                start-view="{{$ctrl.startView}}"
                inline="true"
                is-open="true"
                today="true"
                change="$ctrl.handleDateChange(newValue, oldValue)"
                on-hover-date="$ctrl.handleDateHover(newValue)"
                on-mouse-leave-dates="$ctrl.handleMouseLeaveDates()"
                start-date-range="$ctrl.startMoment"
                end-date-range="$ctrl.endMoment"
                timezone="$ctrl.timezone"
            ></div>
        </div>
    `
})
export class DatePickerComponent implements OnInit, OnChanges {
    @Input('<') locale: string;
    @Input('<') date: Date;
    @Input('<') format: string;
    @Input('<') minView: string;
    @Input('<') maxView: string;
    @Input('<') minDate: Date;
    @Input('<') maxDate: Date;
    @Input('<') startDate: Date | moment.Moment;
    @Input('<') endDate: Date | moment.Moment;
    @Input('<') startView: string;
    @Input('<') timezone: string = '';
    @Input('<') activeViewDate: Date | moment.Moment;

    @Output() onSelect = new EventEmitter<{date: Date, moment: moment.Moment}>();
    @Output() onHoverDate = new EventEmitter<{ date: Date, moment: moment.Moment }>();
    @Output() onMouseEnterDates = new EventEmitter<any>();
    @Output() onMouseLeaveDates = new EventEmitter<any>();
    private selectedMoment: moment.Moment;
    private minMoment: moment.Moment;
    private maxMoment: moment.Moment;
    private startMoment: moment.Moment;
    private endMoment: moment.Moment;
    private activeViewMoment: moment.Moment;

    constructor(@Inject('$translate') private $translate) {
        'ngInject'
    }

    ngOnInit() {
        this.locale = this.locale || this.$translate.use() || 'en';
        this.startView = this.startView ? this.startView : 'month';
        this.minView = this.minView ? this.minView : 'month';
        this.maxView = this.maxView ? this.maxView : 'month';
        this.format = this.format ? this.format : 'L LTS';
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.date && !isDateValueSame(changes.date.currentValue, changes.date.previousValue)) {
            if (changes.date.currentValue) {
                this.selectedMoment = momentTz(changes.date.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.selectedMoment = null;
            }
        }

        if (changes.minDate && !isDateValueSame(changes.minDate.currentValue, changes.minDate.previousValue)) {
            if (changes.minDate.currentValue) {
                this.minMoment = momentTz(changes.minDate.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.minMoment = null;
            }
        }

        if (changes.maxDate && !isDateValueSame(changes.maxDate.currentValue, changes.maxDate.previousValue)) {
            if (changes.maxDate.currentValue) {
                this.maxMoment = momentTz(changes.maxDate.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.maxMoment = null;
            }
        }

        if (changes.startDate && !isDateValueSame(changes.startDate.currentValue, changes.startDate.previousValue)) {
            if (changes.startDate.currentValue) {
                this.startMoment = momentTz(changes.startDate.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.startMoment = null;
            }
        }

        if (changes.endDate && !isDateValueSame(changes.endDate.currentValue, changes.endDate.previousValue)) {
            if (changes.endDate.currentValue) {
                this.endMoment = momentTz(changes.endDate.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.endMoment = null;
            }
        }

        if (changes.activeViewDate && !isDateValueSame(changes.activeViewDate.currentValue, changes.activeViewDate.previousValue)) {
            if (changes.activeViewDate.currentValue) {
                this.activeViewMoment = momentTz(changes.activeViewDate.currentValue).tz(this.timezone).locale(this.locale);
            } else {
                this.activeViewMoment = null;
            }
        }
    }

    handleDateChange(newMoment: moment.Moment, oldMoment: moment.Moment) {
        if (!isDateValueSame(newMoment, oldMoment)) {
            this.onSelect.emit({ date: newMoment ? newMoment.toDate() : null, moment: newMoment });
        }
    }

    handleDateHover(newMoment: moment.Moment) {
        let newDate;
        if (newMoment) {
            newDate = newMoment.toDate();
        }
        this.onHoverDate.emit({date: newDate, moment: newMoment});
    }

    handleMouseLeaveDates() {
        this.onMouseLeaveDates.emit({});
    }
}

function isDateValueSame(currentDate, previousDate) {

    if (currentDate === previousDate || (currentDate && moment(currentDate).isSame(previousDate))) {
        return true;
    }

    return false;
}



// WEBPACK FOOTER //
// ./src/lib/components/date-picker/date-picker.component.ts